/**
 * Inspection helper functions for handling inspection-related functionality
 */

import { sub } from "date-fns";

// Field type constants
export const INSPECTION_FORM_FIELD_TYPE_STATE_WIDGET = 'STATE_WIDGET';
export const INSPECTION_FORM_FIELD_TYPE_BATTERY_STATE_WIDGET = 'BATTERY_STATE_WIDGET';
export const INSPECTION_FORM_FIELD_TYPE_TIRE_STATE_WIDGET = 'TIRE_STATE_WIDGET';
export const INSPECTION_FORM_FIELD_TYPE_DRUM_THICKNESS_WIDGET = 'DRUM_THICKNESS_WIDGET';
export const INSPECTION_FORM_FIELD_TYPE_PAD_THICKNESS_WIDGET = 'PAD_THICKNESS_WIDGET';
export const INSPECTION_FORM_FIELD_TYPE_CHECK = 'CHECK';

/**
 * Gets the literal value of a field based on its type
 * @param {Object} field - The inspection field
 * @returns {string|Object} The literal value
 */
export function getFieldLitteralValue(field) {
  const fieldType = field.inspectionFormSectionField.field_type;

  if (field.value === null || field.value === undefined) {
    return '';
  }

  switch (fieldType) {
    case INSPECTION_FORM_FIELD_TYPE_TIRE_STATE_WIDGET:
    case INSPECTION_FORM_FIELD_TYPE_BATTERY_STATE_WIDGET:
    case INSPECTION_FORM_FIELD_TYPE_DRUM_THICKNESS_WIDGET:
    case INSPECTION_FORM_FIELD_TYPE_PAD_THICKNESS_WIDGET:
      return field.value && typeof field.value === 'object' ? field.value.value : '';
    default:
      return field.value;
  }
}

/**
 * Gets a field value by form field ID
 * @param {string} fieldId - The field ID
 * @param {Object} inspection - The inspection object
 * @returns {string|number} The field value
 */
export function getFieldValueByFormFieldId(fieldId, inspection) {
  const field = inspection.inspectionFields.find((item) => {
    return item.inspectionFormSectionField.id === fieldId;
  });

  if (field) {
    return getFieldLitteralValue(field);
  }

  return 0;
}

/**
 * Evaluates a conditional expression
 * @param {string} expression - The expression to evaluate
 * @param {Object} context - The context containing variables
 * @returns {boolean|null} The result of the evaluation
 */
export function evaluateExpression(expression, context) {
  expression = expression.toLowerCase();
  const lowercaseVariables = {};

  // Create lowercase version of all variables
  Object.entries(context).forEach(([key, value]) => {
    lowercaseVariables[key.toLowerCase()] = value;
  });

  // Replace variables in expression with their values
  Object.entries(lowercaseVariables).forEach(([variable, value]) => {
    // Wrap the value in quotes for string comparison
    expression = expression.replace(variable, `'${value}'`);
  });

  try {
    // Safe evaluation using Function constructor instead of eval
    const result = new Function(`return (${expression});`)();
    return result;
  } catch (error) {
    console.error('Error evaluating expression:', error);
    return null;
  }
}

/**
 * Checks if a field should be visible
 * @param {Object} field - The field to check
 * @returns {boolean} Whether the field should be visible
 */
export function isFieldVisible(field) {
  const formField = field.inspectionFormSectionField;

  if (formField.conditional_field) {
    const conditionExpressions = formField.conditions.map(item =>
      `v${item.field} ${item.operator} '${item.value.toLowerCase()}'`
    );

    const joinedExpressions = conditionExpressions.join(formField.condition_relation);

    const context = {};
    formField.conditions.forEach(condition => {
      context[`v${condition.field}`] = getFieldValueByFormFieldId(
        condition.field,
        field.inspection
      ).toLowerCase();
    });

    return evaluateExpression(joinedExpressions, context);
  }

  return isSubSectionVisible(
    field.inspectionFormSectionField.inspectionFormSection,
    field.inspection
  );
}

/**
 * Checks if a subsection should be visible
 * @param {Object} subSection - The subsection to check
 * @param {Object} inspection - The inspection object
 * @returns {boolean} Whether the subsection should be visible
 */
export function isSubSectionVisible(subSection, inspection) {
  if (subSection.conditional_field) {
    const conditionExpressions = subSection.conditions.map(item =>
      `v${item.field} ${item.operator} '${item.value.toLowerCase()}'`
    );

    const joinedExpressions = conditionExpressions.join(subSection.condition_relation);

    const context = {};
    subSection.conditions.forEach(condition => {
      context[`v${condition.field}`] = getFieldValueByFormFieldId(
        condition.field,
        inspection
      ).toLowerCase();
    });

    return evaluateExpression(joinedExpressions, context);
  }

  return isSectionVisible(subSection.parent, inspection);
}

/**
 * Checks if a section should be visible
 * @param {Object} section - The section to check
 * @param {Object} inspection - The inspection object
 * @returns {boolean} Whether the section should be visible
 */
export function isSectionVisible(section, inspection) {
  if (section.conditional_field) {
    const conditionExpressions = section.conditions.map(item =>
      `v${item.field} ${item.operator} '${item.value.toLowerCase()}'`
    );

    const joinedExpressions = conditionExpressions.join(section.condition_relation);

    const context = {};
    section.conditions.forEach(condition => {
      context[`v${condition.field}`] = getFieldValueByFormFieldId(
        condition.field,
        inspection
      ).toLowerCase();
    });

    return evaluateExpression(joinedExpressions, context);
  }

  return true;
}

/**
 * Gets the default "good" value for a field based on its type
 * @param {Object} field - The field to get a good value for
 * @returns {string|Object} The good value
 */
export function getGoodFieldValue(field) {
  if (field.field_type === INSPECTION_FORM_FIELD_TYPE_STATE_WIDGET) {
    return 'GOOD';
  }

  if (field.field_type === INSPECTION_FORM_FIELD_TYPE_BATTERY_STATE_WIDGET) {
    return { value: 'GOOD', charge: 1, total_charge: 1 };
  }

  if (field.field_type === INSPECTION_FORM_FIELD_TYPE_TIRE_STATE_WIDGET) {
    return {
      value: 'GOOD',
      ratio: 11,
      type: 'STANDARD',
      season: 'SUMMER',
    };
  }

  if (field.field_type === INSPECTION_FORM_FIELD_TYPE_DRUM_THICKNESS_WIDGET) {
    return {
      value: 'GOOD',
      ratio: 10,
    };
  }

  if (field.field_type === INSPECTION_FORM_FIELD_TYPE_PAD_THICKNESS_WIDGET) {
    return {
      value: 'GOOD',
      ratio: 10,
      type: 'STANDARD',
    };
  }

  if (field.field_type === INSPECTION_FORM_FIELD_TYPE_CHECK) {
    const pointsThreshold = field.points_threshold;
    const option = pointsThreshold.reduce((max, obj) =>
      obj.points > max.points ? obj : max,
      pointsThreshold[0]
    );

    return option.option;
  }

  // Default case for other field types
  const pointsThreshold = field.points_threshold;
  const option = pointsThreshold.reduce((max, obj) =>
    obj.points > max.points ? obj : max,
    pointsThreshold[0]
  );

  return option.option;
}

/**
 * Gets section scores for an inspection
 * @param {Object} inspection - The inspection object
 * @param {boolean} recalculate - Whether to force recalculation (parameter kept for API consistency)
 * @returns {Object} The section scores
 */
export function getSectionsScores(inspection, recalculate = false) {
  // Only check scoreData, removed all caching logic
  if (
    inspection.status === 'COMPLETED' &&
    !recalculate &&
    inspection.scoreData &&
    inspection.scoreData.sectionsScores
  ) {
    return inspection.scoreData.sectionsScores;
  }

  const inspectionSectionPoints = {};
  const inspectionForm = inspection.inspectionForm;

  // Process each section
  for (const section of inspectionForm.inspectionFormSections) {

    if (!isSectionVisible(section, inspection)) {
      continue;
    }

    let sectionPoints = 0;
    let sectionMaxPoints = 0;
    let totalSectionFactors = 0;
    let totalSectionFactoredScores = 0;
    const inspectionSubSectionPoints = {};
    let sectionFilled = true;

    // Process each subsection
    for (const subSection of section.subSections) {

      if (!isSubSectionVisible(subSection, inspection)) {
        continue;
      }

      let subSectionPoints = 0;
      let subSectionMaxPoints = 0;
      let subSectionFilled = true;
      const formFields = {};

      // Process each field in the subsection
      for (const field of subSection.inspectionFormSectionFields) {
        const inspectionField = inspection.inspectionFields.find((item) => {
          return item.inspectionFormSectionField.id === field.id;
        });

        if (inspectionField) {
          // Calculate points
          sectionPoints += getPoints(inspectionField);
          sectionMaxPoints += getMaxPoints(field);
          subSectionPoints += getPoints(inspectionField);
          subSectionMaxPoints += getMaxPoints(field);

          // Build field data
          formFields[field.id] = {
            id: field.id,
            inspection_field_id: inspectionField.id,
            name: field.name,
            field_type: field.field_type,
            points: getPoints(inspectionField),
            maxPoints: getMaxPoints(field),
            score: getScore(inspectionField),
            action: inspectionField.action,
            order_number: field.order_number,
            value: inspectionField.value,
            touched: inspectionField.touched,
            images: inspectionField.images,
            can_upload_image: field.can_upload_image,
            comment: inspectionField.comment,
            can_add_comment: field.can_add_comment,
            points_threshold: field.points_threshold,
            enable_na: field.enable_na,
            is_copy: field.is_copy
          };

          // Check if field is touched
          if (inspectionField.touched === false) {
            subSectionFilled = false;
            sectionFilled = false;
          }
        }
      }

      // Calculate subsection score
      const score = subSectionMaxPoints ? Math.round((subSectionPoints / subSectionMaxPoints) * 100) : 100;

      // Build subsection data
      inspectionSubSectionPoints[subSection.id] = {
        id: subSection.id,
        name: subSection.name,
        icon: section.icon,
        points: subSectionPoints,
        maxPoints: subSectionMaxPoints,
        factor: subSection.factor,
        score: score,
        fields: formFields,
        order_number: subSection.order_number,
        filled: subSectionFilled
      };

      // Calculate section totals
      totalSectionFactors += subSection.factor;
      totalSectionFactoredScores += (score * subSection.factor);
    }

    // Build section data
    inspectionSectionPoints[section.id] = {
      id: section.id,
      name: section.name,
      icon: section.icon,
      points: sectionPoints,
      maxPoints: sectionMaxPoints,
      factor: section.factor,
      score: Math.round(totalSectionFactoredScores / totalSectionFactors),
      subSections: inspectionSubSectionPoints,
      order_number: section.order_number,
      touched: Array.isArray(inspection.touched_sections) && inspection.touched_sections.includes(section.id.toString()),
      filled: sectionFilled
    };
  }

  return inspectionSectionPoints;
}

/**
 * Calculates points for a field based on its type and value
 * @param {Object} field - The inspection field object
 * @returns {number} The calculated points
 */
export function getPoints(field) {
  // Extract field type and points threshold from fieldData
  const fieldType = field.fieldData?.field_type || field.inspectionFormSectionField?.field_type;
  const pointsThreshold = field.fieldData?.points_threshold || field.inspectionFormSectionField?.points_threshold || [];
  const value = field.value;

  if (fieldType === 'CHECK') {
    const optionsMap = {};
    pointsThreshold.forEach(item => {
      optionsMap[item.option] = item;
    });

    if (value) {
      if (optionsMap['YES'] && value === 'YES') {
        return parseInt(optionsMap['YES'].points);
      } else if (optionsMap['NO'] && value === 'NO') {
        return parseInt(optionsMap['NO'].points);
      }
    } else {
      if (optionsMap['NO']) {
        return parseInt(optionsMap['NO'].points);
      }
    }

    return 0;
  }

  if (fieldType === 'SELECT_SINGLE') {
    for (const item of pointsThreshold) {
      if (item.option === value) {
        return parseInt(item.points);
      }
    }
  }

  if (fieldType === 'SELECT_MULTIPLE') {
    let points = 0;

    if (pointsThreshold) {
      for (const item of pointsThreshold) {
        if (value && value[item.option]) {
          points += parseInt(item.points);
        }
      }
    }

    return points;
  }

  if (fieldType === 'NUMBER') {
    for (const item of pointsThreshold) {
      const [min, max] = item.option.split('-');
      if (value >= parseFloat(min) && value <= parseFloat(max)) {
        return parseInt(item.points);
      }
    }
  }

  if (fieldType === 'RANGE_SLIDER') {
    for (const item of pointsThreshold) {
      const [min, max] = item.option.split('-');
      if (value >= parseFloat(min) && value <= parseFloat(max)) {
        return parseInt(item.points);
      }
    }
  }

  if (fieldType === INSPECTION_FORM_FIELD_TYPE_STATE_WIDGET) {
    const optionsMap = {};
    pointsThreshold.forEach(item => {
      optionsMap[item.option] = item;
    });

    if (optionsMap[value]) {
      return parseInt(optionsMap[value].points);
    }

    return 0;
  }

  if (fieldType === INSPECTION_FORM_FIELD_TYPE_TIRE_STATE_WIDGET ||
    fieldType === INSPECTION_FORM_FIELD_TYPE_DRUM_THICKNESS_WIDGET ||
    fieldType === INSPECTION_FORM_FIELD_TYPE_PAD_THICKNESS_WIDGET ||
    fieldType === INSPECTION_FORM_FIELD_TYPE_BATTERY_STATE_WIDGET) {

    const optionsMap = {};
    pointsThreshold.forEach(item => {
      optionsMap[item.option] = item;
    });

    if (value != null) {
      const widgetValue = typeof value === 'object' ? value.value : null;

      if (widgetValue && optionsMap[widgetValue]) {
        return parseInt(optionsMap[widgetValue].points);
      }
    }

    return 0;
  }

  return 0;
}

/**
 * Gets maximum possible points for a field
 * @param {Object} field - The field object
 * @returns {number} Maximum possible points
 */
export function getMaxPoints(field) {
  const pointsThreshold = field.points_threshold || [];
  let maxPoints = 0;

  for (const item of pointsThreshold) {
    const points = parseInt(item.points);
    if (points > maxPoints) {
      maxPoints = points;
    }
  }

  return maxPoints;
}

/**
 * Calculates score as a percentage
 * @param {Object} field - The inspection field
 * @returns {number} Score as percentage
 */
export function getScore(field) {
  const totalPoints = getMaxPoints(field.inspectionFormSectionField || field.fieldData);

  if (totalPoints === 0) {
    return 100;
  }

  return Math.round((getPoints(field) / totalPoints) * 100);
}

/**
 * Updates linked fields with the same values
 * @param {Object} field - The source field that was changed
 * @param {Object} inspection - The inspection object containing all fields
 * @returns {Array} Array of fields that were updated
 */
export function updateLinkedFields(field, inspection) {
  // Check if field type can be linked
  const canBeLinked = [
    INSPECTION_FORM_FIELD_TYPE_TIRE_STATE_WIDGET,
    INSPECTION_FORM_FIELD_TYPE_DRUM_THICKNESS_WIDGET,
    INSPECTION_FORM_FIELD_TYPE_PAD_THICKNESS_WIDGET,
  ];

  const fieldType = field.field_type;

  if (!canBeLinked.includes(fieldType)) {
    return [];
  }

  // Get section ID from the field
  let subSectionId = null;
  for (const section of Object.values(inspection.sectionsScores || {})) {
    for (const subSection of Object.values(section.subSections)) {
      if (Object.values(subSection.fields).some(f => f.inspection_field_id == field.id)) {
        subSectionId = subSection.id;
        break;
      }
    }
    if (subSectionId) break;
  }

  if (!subSectionId) return [];

  // Find all fields in the same section with the same field type
  const linkedFields = [];
  for (const section of Object.values(inspection.sectionsScores || {})) {
    for (const subSection of Object.values(section.subSections)) {
      if (subSection.id == subSectionId) {
        const fieldsInSubSection = Object.values(subSection.fields)
          .filter(f => f.field_type === fieldType)
          .sort((a, b) => a.order_number - b.order_number);

        if (fieldsInSubSection.length > 0) {
          linkedFields.push(...fieldsInSubSection);
        }
      }
    }
  }

  // If this is the first field by order_number and there are multiple fields,
  // update all other fields
  if (linkedFields.length > 1) {
    // Sort by order number
    linkedFields.sort((a, b) => a.order_number - b.order_number);

    // If the current field is the first one
    if (linkedFields[0].inspection_field_id == field.id) {
      const fieldsToUpdate = linkedFields.slice(1);
      const updatedFields = [];

      // Update all other fields with the same values
      for (const fieldToUpdate of fieldsToUpdate) {
        const inspectionField = inspection.inspectionFields.find(f => f.id == fieldToUpdate.inspection_field_id);
        if (inspectionField) {
          inspectionField.value = field.value;
          inspectionField.action = field.action;
          inspectionField.touched = true;
          updatedFields.push(inspectionField);
        }
      }

      return updatedFields;
    }
  }

  return [];
}
